/**
 * KatoContext — выбранный КАТО + дерево КАТО.
 *
 * `selectedKato` персистится в `localStorage`. При первом монтировании
 * восстанавливается оттуда, иначе дефолтится по `UserContext.kato_access`:
 *  - super-admin (`[0]`) → 150000000 (дефолтная область);
 *  - иначе — первый доступный КАТО юзера.
 *
 * `tree` — дерево из `/api/public/kato/tree`. Endpoint пока не реализован
 * на backend (см. §5.6 ТЗ + footnote в 04_functional_scope.md); до его
 * появления `tree = []` без 404-ошибки в UI.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useUser } from './UserContext';
import { isKatoInScope } from '../lib/user-access';
import type { KatoNode } from '../types/kato';

const STORAGE_KEY = 'dp-v2:selected-kato';
export const DEFAULT_OBLAST_KATO = 150000000;

interface KatoContextValue {
  selectedKato: number | null;
  setSelectedKato: (kato: number) => void;
  tree: KatoNode[];
}

const KatoContext = createContext<KatoContextValue | null>(null);

const readStoredKato = (): number | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const storeKato = (kato: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, String(kato));
  } catch {
    // ignore (private mode и т.п.)
  }
};

/**
 * Выбранный КАТО с учётом прав пользователя.
 *
 * Сохранённое значение переживает смену учётки, поэтому его нельзя принимать
 * на веру: после входа под пользователем с другой территорией в localStorage
 * остаётся чужой КАТО, справочник его больше не содержит — селектор пустой,
 * все виджеты отвечают 403, и это выглядит как «у пользователя нет КАТО».
 * Не свой КАТО заменяем первой доступной территорией.
 */
const pickDefaultKato = (
  storedKato: number | null,
  userKatoAccess: number[] | undefined,
): number | null => {
  if (storedKato !== null && isKatoInScope(storedKato, userKatoAccess)) {
    return storedKato;
  }
  if (!userKatoAccess || userKatoAccess.length === 0) return storedKato;
  // Super-admin → дефолт областной КАТО.
  if (userKatoAccess.includes(0)) return storedKato ?? DEFAULT_OBLAST_KATO;
  return userKatoAccess[0];
};

export const KatoProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const [selectedKato, setSelectedKatoState] = useState<number | null>(
    () => readStoredKato(),
  );
  // Дерево пока пустое — endpoint /api/public/kato/tree отложен.
  const tree = useMemo<KatoNode[]>(() => [], []);

  // Заполнить selectedKato из user.kato_access — и починить сохранённый, если
  // он оказался вне прав текущего пользователя (смена учётки, отзыв роли).
  useEffect(() => {
    const picked = pickDefaultKato(selectedKato, user?.kato_access);
    if (picked !== null && picked !== selectedKato) {
      setSelectedKatoState(picked);
      storeKato(picked);
    }
  }, [selectedKato, user?.kato_access]);

  const setSelectedKato = useCallback(
    (kato: number) => {
      // Чужой КАТО не принимаем даже из URL: иначе он тут же уедет в
      // localStorage и переживёт перезагрузку.
      const next = pickDefaultKato(kato, user?.kato_access);
      if (next === null) return;
      setSelectedKatoState(next);
      storeKato(next);
    },
    [user?.kato_access],
  );

  return (
    <KatoContext.Provider value={{ selectedKato, setSelectedKato, tree }}>
      {children}
    </KatoContext.Provider>
  );
};

export const useKato = (): KatoContextValue => {
  const ctx = useContext(KatoContext);
  if (!ctx) {
    throw new Error('useKato must be used inside <KatoProvider>');
  }
  return ctx;
};
